import { useMemo } from "react";
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, ComposedChart } from "recharts";
import { calculateMetricsFromTasks } from "@/lib/utils";
import type { Metrics, Task } from "@/types/dashboard";

interface AccuracyTrendProps {
  metrics: Metrics | null;
  tasks?: Record<string, Task>;
  totalQuestions?: number;
}

// Custom tooltip with glassmorphism
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="glass-tooltip">
        <p className="font-semibold text-amber-200">Question {data.index}</p>
        <p className="text-sm text-amber-100">
          Average: <span className="font-bold bg-gradient-to-r from-amber-300 to-orange-300 bg-clip-text text-transparent">
            {data.runningAvg?.toFixed(2) ?? '0.00'}
          </span>/5.0
        </p>
      </div>
    );
  }
  return null;
};

export function AccuracyTrend({ metrics: propMetrics, tasks, totalQuestions }: AccuracyTrendProps) {
  // Calculate metrics from tasks (single source of truth)
  const metrics = useMemo(() => {
    if (tasks && Object.keys(tasks).length > 0) {
      return calculateMetricsFromTasks(tasks, totalQuestions);
    }
    return propMetrics;
  }, [tasks, totalQuestions, propMetrics]);

  const chartData = useMemo(() => {
    if (!metrics || !metrics.scores || metrics.scores.length === 0) return [];

    const data: { index: number; score: number; runningAvg: number }[] = [];
    let sum = 0;

    metrics.scores.forEach((score, index) => {
      sum += score;
      const runningAvg = sum / (index + 1);
      data.push({
        index: index + 1,
        score,
        runningAvg,
      });
    });

    return data;
  }, [metrics]);

  if (!metrics || chartData.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Waiting for evaluation data...
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
          Average Score Trend
        </h3>
        <div className="text-xs text-muted-foreground">
          Current: <span className="font-bold text-lg bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
            {metrics.mean_accuracy?.toFixed(2) ?? '0.00'}
          </span>/5.0
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
            <defs>
              <linearGradient id="colorAvg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#a78bfa" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(168, 139, 250, 0.1)" />
            <XAxis
              dataKey="index"
              tick={{ fill: 'rgba(255, 255, 255, 0.6)', fontSize: 11 }}
              stroke="rgba(168, 139, 250, 0.3)"
              label={{
                value: 'Question #',
                position: 'insideBottom',
                offset: -15,
                style: { fill: 'rgba(255, 255, 255, 0.6)', fontSize: 11 }
              }}
            />
            <YAxis
              domain={[0, 5]}
              ticks={[0, 1, 2, 3, 4, 5]}
              tick={{ fill: 'rgba(255, 255, 255, 0.6)', fontSize: 11 }}
              stroke="rgba(168, 139, 250, 0.3)"
              label={{
                value: 'Score',
                angle: -90,
                position: 'insideLeft',
                style: { fill: 'rgba(255, 255, 255, 0.6)', fontSize: 11 }
              }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(168, 139, 250, 0.3)', strokeWidth: 2 }} />
            <ReferenceLine
              y={metrics.mean_accuracy}
              stroke="#f472b6"
              strokeDasharray="5 5"
              strokeWidth={2}
              label={{
                value: `Avg: ${metrics.mean_accuracy?.toFixed(2) ?? '0.00'}`,
                position: 'right',
                fill: '#f9a8d4',
                fontSize: 11,
                fontWeight: 'bold'
              }}
            />
            <Area
              type="monotone"
              dataKey="runningAvg"
              fill="url(#colorAvg)"
              stroke="none"
            />
            <Line
              type="monotone"
              dataKey="runningAvg"
              stroke="#a78bfa"
              strokeWidth={3}
              dot={{ fill: '#a78bfa', r: 4, strokeWidth: 2, stroke: '#1e1b4b' }}
              activeDot={{ r: 6, fill: '#c084fc', stroke: '#a78bfa', strokeWidth: 2 }}
              name="Running Average"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
